import { setLoading, addAlert } from "src/modules/notification/redux";
import { getErrorMessage } from "src/utils/errorMessages";
import {
  getAccessRights,
  getCompanies,
  getCompanyPlanInfo,
  getNodesWithWhiteLabel,
  updateAccessRights,
  updateCompanyPlan,
  putBranchWhiteLabel,
  removeBranchWhiteLabel,
  getBranchListByProduct,
  getProductList,
  updateAssign,
  updateUnassign,
  getNodeSnRange,
  getSnFormatsByBranch,
  checkSnAssignAvailability,
  getBundleOptions,
  setCompanyBundle,
} from "../action/companies.action";

// The bundle apis reject with the SERVER'S message, so a rejected read/write carries the real
// reason in action.payload. Same helper (and same rule — do not replace it with a guess) as
// bundles.middleware.
const failureMessage = (action, fallback) => action.payload || fallback;

// Not every dispatch of a thunk is something the admin asked for. resolveBundleId re-reads
// bundle-options between a successful create and the assign, to pick up the server's own
// record of the bundle it just made — the admin never asked for that read, and its failure
// says nothing about whether their save worked (the create is already done and the id is
// handed on regardless). Such a dispatch opts out by passing the flag; a first load
// (dispatched with no argument at all) does not, and is still alerted.
const isErrorAlertSuppressed = (action) => Boolean(
  action.meta && action.meta.arg && action.meta.arg.suppressErrorAlert
);

const getCompaniesMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    const { payload, type } = action;

    switch (type) {
      case getCompanies.pending.type:
        dispatch(setLoading({ id: getCompanies.typePrefix, state: true }));
        break;
      case getCompanies.fulfilled.type:
        dispatch(setLoading({ id: getCompanies.typePrefix, state: false }));
        break;
      // The sole owner of this rejection since the generic loading middleware opted it out
      // (it was firing a second, different message for the same failure). Two consequences,
      // both of them here: the message has to be a real string — `payload.code` is an axios
      // error code when it is defined at all, and reading `.code` off an undefined payload
      // threw inside the middleware — and the post-save list refresh, which the admin never
      // asked for, has to be able to stay quiet. A stale list is not a failed save.
      case getCompanies.rejected.type:
        dispatch(setLoading({ id: getCompanies.typePrefix, state: false }));
        if (!isErrorAlertSuppressed(action)) {
          dispatch(
            addAlert({
              severity: "error",
              message: getErrorMessage(payload),
            })
          );
        }
        break;
      default:
        break;
    }
  };

const getAccessRightsMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    const { payload, type } = action;

    switch (type) {
      case getAccessRights.pending.type:
        dispatch(setLoading({ id: getAccessRights.typePrefix, state: true }));
        break;
      case getAccessRights.fulfilled.type:
        dispatch(setLoading({ id: getAccessRights.typePrefix, state: false }));
        break;
      case getAccessRights.rejected.type:
        dispatch(setLoading({ id: getAccessRights.typePrefix, state: false }));
        dispatch(
          addAlert({
            severity: "error",
            message: payload.code,
          })
        );
        break;
      default:
        break;
    }
  };

const updateAccessRightsMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    const { payload, type } = action;

    switch (type) {
      case updateAccessRights.pending.type:
        dispatch(
          setLoading({ id: updateAccessRights.typePrefix, state: true })
        );
        break;
      case updateAccessRights.fulfilled.type:
        dispatch(
          setLoading({ id: updateAccessRights.typePrefix, state: false })
        );
        break;
      case updateAccessRights.rejected.type:
        dispatch(
          setLoading({ id: updateAccessRights.typePrefix, state: false })
        );
        dispatch(
          addAlert({
            severity: "error",
            message: payload.code,
          })
        );
        break;
      default:
        break;
    }
  };

const getCompanyPlanInfoMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    const { payload, type } = action;

    switch (type) {
      case getCompanyPlanInfo.pending.type:
        dispatch(
          setLoading({ id: getCompanyPlanInfo.typePrefix, state: true })
        );
        break;
      case getCompanyPlanInfo.fulfilled.type:
        dispatch(
          setLoading({ id: getCompanyPlanInfo.typePrefix, state: false })
        );
        break;
      case getCompanyPlanInfo.rejected.type:
        dispatch(
          setLoading({ id: getCompanyPlanInfo.typePrefix, state: false })
        );
        dispatch(
          addAlert({
            severity: "error",
            message: payload.code,
          })
        );
        break;
      default:
        break;
    }
  };

const updateCompanyPlanMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    const { payload, type } = action;

    switch (type) {
      case updateCompanyPlan.pending.type:
        dispatch(setLoading({ id: updateCompanyPlan.typePrefix, state: true }));
        break;
      case updateCompanyPlan.fulfilled.type:
        dispatch(
          setLoading({ id: updateCompanyPlan.typePrefix, state: false })
        );
        break;
      case updateCompanyPlan.rejected.type:
        dispatch(
          setLoading({ id: updateCompanyPlan.typePrefix, state: false })
        );
        dispatch(
          addAlert({
            severity: "error",
            message: payload.code,
          })
        );
        break;
      default:
        break;
    }
  };

const getNodesWithWhiteLabelMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    const { payload, type } = action;

    switch (type) {
      case getNodesWithWhiteLabel.pending.type:
        dispatch(
          setLoading({ id: getNodesWithWhiteLabel.typePrefix, state: true })
        );
        break;
      case getNodesWithWhiteLabel.fulfilled.type:
        dispatch(
          setLoading({ id: getNodesWithWhiteLabel.typePrefix, state: false })
        );
        break;
      case getNodesWithWhiteLabel.rejected.type:
        dispatch(
          setLoading({ id: getNodesWithWhiteLabel.typePrefix, state: false })
        );
        dispatch(
          addAlert({
            severity: "error",
            message: payload.code,
          })
        );
        break;
      default:
        break;
    }
  };

const putBranchWhiteLabelMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    const { payload, type } = action;

    switch (type) {
      case putBranchWhiteLabel.pending.type:
        dispatch(
          setLoading({ id: putBranchWhiteLabel.typePrefix, state: true })
        );
        break;
      case putBranchWhiteLabel.fulfilled.type:
        dispatch(
          setLoading({ id: putBranchWhiteLabel.typePrefix, state: false })
        );
        break;
      case putBranchWhiteLabel.rejected.type:
        dispatch(
          setLoading({ id: putBranchWhiteLabel.typePrefix, state: false })
        );
        dispatch(
          addAlert({
            severity: "error",
            message: payload.data.white_label,
          })
        );
        break;
      default:
        break;
    }
  };

const removeBranchWhiteLabelMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    const { payload, type } = action;

    switch (type) {
      case removeBranchWhiteLabel.pending.type:
        dispatch(
          setLoading({ id: removeBranchWhiteLabel.typePrefix, state: true })
        );
        break;
      case removeBranchWhiteLabel.fulfilled.type:
        dispatch(
          setLoading({ id: removeBranchWhiteLabel.typePrefix, state: false })
        );
        break;
      case removeBranchWhiteLabel.rejected.type:
        dispatch(
          setLoading({ id: removeBranchWhiteLabel.typePrefix, state: false })
        );
        dispatch(
          addAlert({
            severity: "error",
            message: payload.code,
          })
        );
        break;
      default:
        break;
    }
  };

const getBranchListMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    const { payload, type } = action;

    switch (type) {
      case getBranchListByProduct.pending.type:
        dispatch(
          setLoading({ id: getBranchListByProduct.typePrefix, state: true })
        );
        break;
      case getBranchListByProduct.fulfilled.type:
        dispatch(
          setLoading({ id: getBranchListByProduct.typePrefix, state: false })
        );
        break;
      case getBranchListByProduct.rejected.type:
        dispatch(
          setLoading({ id: getBranchListByProduct.typePrefix, state: false })
        );
        dispatch(
          addAlert({
            severity: "error",
            message: payload.code,
          })
        );
        break;
      default:
        break;
    }
  };

const getProductListMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    const { payload, type } = action;

    switch (type) {
      case getProductList.pending.type:
        dispatch(setLoading({ id: getProductList.typePrefix, state: true }));
        break;
      case getProductList.fulfilled.type:
        dispatch(setLoading({ id: getProductList.typePrefix, state: false }));
        break;
      case getProductList.rejected.type:
        dispatch(setLoading({ id: getProductList.typePrefix, state: false }));
        dispatch(
          addAlert({
            severity: "error",
            message: payload.code,
          })
        );
        break;
      default:
        break;
    }
  };

const updateAssignMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    const { payload, type } = action;

    switch (type) {
      case updateAssign.pending.type:
        dispatch(setLoading({ id: updateAssign.typePrefix, state: true }));
        break;
      case updateAssign.fulfilled.type:
        dispatch(setLoading({ id: updateAssign.typePrefix, state: false }));
        break;
      case updateAssign.rejected.type:
        dispatch(setLoading({ id: updateAssign.typePrefix, state: false }));
        dispatch(
          addAlert({
            severity: "error",
            message:
              payload && payload.code === "error.ERROR_ASSIGN_FAILED"
                ? "Serial number assign failed. Please try again later"
                : payload && payload.message
                ? payload.message
                : payload && payload.code
                ? payload.code
                : "An error occurred",
          })
        );
        break;
      default:
        break;
    }
  };

const updateUnassignMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    const { payload, type } = action;

    switch (type) {
      case updateUnassign.pending.type:
        dispatch(setLoading({ id: updateUnassign.typePrefix, state: true }));
        break;
      case updateUnassign.fulfilled.type:
        dispatch(setLoading({ id: updateUnassign.typePrefix, state: false }));
        break;
      case updateUnassign.rejected.type:
        dispatch(setLoading({ id: updateUnassign.typePrefix, state: false }));
        dispatch(
          addAlert({
            severity: "error",
            message: payload.code,
          })
        );
        break;
      default:
        break;
    }
  };

const getNodeSnRangeMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    const { payload, type } = action;

    switch (type) {
      case getNodeSnRange.pending.type:
        dispatch(setLoading({ id: getNodeSnRange.typePrefix, state: true }));
        break;
      case getNodeSnRange.fulfilled.type:
        dispatch(setLoading({ id: getNodeSnRange.typePrefix, state: false }));
        break;
      case getNodeSnRange.rejected.type:
        dispatch(setLoading({ id: getNodeSnRange.typePrefix, state: false }));
        dispatch(
          addAlert({
            severity: "error",
            message: payload.code,
          })
        );
        break;
      default:
        break;
    }
  };

const getSnFormatsByBranchMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    const { payload, type } = action;

    switch (type) {
      case getSnFormatsByBranch.pending.type:
        dispatch(
          setLoading({ id: getSnFormatsByBranch.typePrefix, state: true })
        );
        break;
      case getSnFormatsByBranch.fulfilled.type:
        dispatch(
          setLoading({ id: getSnFormatsByBranch.typePrefix, state: false })
        );
        break;
      case getSnFormatsByBranch.rejected.type:
        dispatch(
          setLoading({ id: getSnFormatsByBranch.typePrefix, state: false })
        );
        dispatch(
          addAlert({
            severity: "error",
            message: payload.code,
          })
        );
        break;
      default:
        break;
    }
  };

const checkSnAssignAvailabilityMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    const { payload, type } = action;

    switch (type) {
      case checkSnAssignAvailability.pending.type:
        dispatch(
          setLoading({ id: checkSnAssignAvailability.typePrefix, state: true })
        );
        break;
      case checkSnAssignAvailability.fulfilled.type:
        dispatch(
          setLoading({ id: checkSnAssignAvailability.typePrefix, state: false })
        );
        break;
      case checkSnAssignAvailability.rejected.type:
        dispatch(
          setLoading({ id: checkSnAssignAvailability.typePrefix, state: false })
        );
        dispatch(
          addAlert({
            severity: "error",
            message: payload.code,
          })
        );
        break;
      default:
        break;
    }
  };

// Assigning a bundle to a company. This is the ONE place all three bundle surfaces (the
// approval wizard's company-feature sibling, the standalone Company Bundle dialog and
// Companies > View > Company Features) end up, so it is the one place that has to speak up.
//
// It matters more here than for a plain write: a save whose selection matches no live bundle
// CREATES the bundle first, and the bundles middleware has already put a green "Bundle
// Created successfully" on this same snackbar by the time the assign runs. Without the
// rejected case below, a create-succeeded-assign-failed save reads as an unqualified success
// while the company still holds its old bundle.
const setCompanyBundleMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    const { type } = action;

    switch (type) {
      case setCompanyBundle.pending.type:
        dispatch(setLoading({ id: setCompanyBundle.typePrefix, state: true }));
        break;
      case setCompanyBundle.fulfilled.type:
        dispatch(setLoading({ id: setCompanyBundle.typePrefix, state: false }));
        break;
      case setCompanyBundle.rejected.type:
        dispatch(setLoading({ id: setCompanyBundle.typePrefix, state: false }));
        dispatch(
          addAlert({
            severity: "error",
            message: failureMessage(
              action,
              "Failed to assign the bundle to this company"
            ),
          })
        );
        break;
      default:
        break;
    }
  };

// The bundle dropdown every surface is built from. Nothing tracked this before, so a failed
// GET left an empty list that the editors could only read as "no bundle is live" — and said
// nothing about it. The loading flag is what BundleSelectionEditor waits on.
//
// The rejection alert is for a read the admin is WAITING on — opening the panel with no
// dropdown to show. It is deliberately NOT fired for the post-create cache warm-up inside
// resolveBundleId: there, the create and the assign may both have succeeded, and a red
// sticky toast for a refresh that failed afterwards would tell the admin their save failed
// when it did not. That dispatch marks itself with suppressErrorAlert.
const getBundleOptionsMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    next(action);

    const { type } = action;

    switch (type) {
      case getBundleOptions.pending.type:
        dispatch(setLoading({ id: getBundleOptions.typePrefix, state: true }));
        break;
      case getBundleOptions.fulfilled.type:
        dispatch(setLoading({ id: getBundleOptions.typePrefix, state: false }));
        break;
      case getBundleOptions.rejected.type:
        dispatch(setLoading({ id: getBundleOptions.typePrefix, state: false }));
        if (!isErrorAlertSuppressed(action)) {
          dispatch(
            addAlert({
              severity: "error",
              message: failureMessage(action, "Failed to load bundle options"),
            })
          );
        }
        break;
      default:
        break;
    }
  };

export default [
  getCompaniesMiddleware,
  getAccessRightsMiddleware,
  updateAccessRightsMiddleware,
  getCompanyPlanInfoMiddleware,
  updateCompanyPlanMiddleware,
  getNodesWithWhiteLabelMiddleware,
  putBranchWhiteLabelMiddleware,
  removeBranchWhiteLabelMiddleware,
  getBranchListMiddleware,
  getProductListMiddleware,
  updateAssignMiddleware,
  updateUnassignMiddleware,
  getNodeSnRangeMiddleware,
  getSnFormatsByBranchMiddleware,
  checkSnAssignAvailabilityMiddleware,
  setCompanyBundleMiddleware,
  getBundleOptionsMiddleware,
];
